import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useRoleGuard } from "@/hooks/use-role-guard";
import { useServerFn } from "@tanstack/react-start";
import {
  listOrders,
  listAssignableAdmins,
  assignOrder,
  listAbandonedCheckouts,
  getCompletionReportUrl,
  resendCompletionEmail,
  type OrderRow,
  type AbandonedCheckoutRow,
} from "@/lib/admin.functions";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { AddUserDialog, AddTaskDialog } from "@/components/admin-offline-order";

function fmtAmount(cents: number | null, currency: string | null) {
  if (cents == null) return "-";
  const c = (currency ?? "usd").toUpperCase();
  const sym = c === "USD" ? "$" : c === "INR" ? "₹" : `${c} `;
  return `${sym}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const Route = createFileRoute("/admin/orders")({
  head: () => ({
    meta: [
      { title: "Orders - Admin - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OrdersPage,
});

const UNASSIGNED = "__unassigned__";

type Admin = { id: string; email: string; role: "admin" | "super_admin" };

const statusLabel: Record<OrderRow["fulfillment_status"], string> = {
  unassigned: "Unassigned",
  assigned: "Assigned",
  in_progress: "In progress",
  completed: "Completed",
};

function StatusBadge({ status }: { status: OrderRow["fulfillment_status"] }) {
  const tone =
    status === "completed"
      ? "bg-[#DDF0DA] text-[#2E5B2E]"
      : status === "in_progress"
        ? "bg-[#FDE9C7] text-[#8A5A16]"
        : status === "assigned"
          ? "bg-accent-soft text-accent-ink"
          : "bg-surface-sunken text-ink-tertiary";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`}>
      {statusLabel[status]}
    </span>
  );
}

function isOverdue(o: OrderRow): boolean {
  if (o.fulfillment_status === "completed") return false;
  const base = o.paid_at ?? o.created_at;
  if (!base) return false;
  return Date.now() > new Date(base).getTime() + 48 * 60 * 60 * 1000;
}

function OrdersPage() {
  const { status: roleStatus } = useRoleGuard(["admin", "super_admin"]);
  const runList = useServerFn(listOrders);
  const runAdmins = useServerFn(listAssignableAdmins);
  const runAssign = useServerFn(assignOrder);
  const runAbandoned = useServerFn(listAbandonedCheckouts);
  const runGetReportUrl = useServerFn(getCompletionReportUrl);
  const runResendCompletion = useServerFn(resendCompletionEmail);

  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [abandoned, setAbandoned] = useState<AbandonedCheckoutRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [payFilter, setPayFilter] = useState<"all" | "paid" | "pending" | "abandoned">("all");
  const [fulfilFilter, setFulfilFilter] = useState<"all" | OrderRow["fulfillment_status"]>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "deadline" | "company">("newest");
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const [o, a, ab] = await Promise.all([runList(), runAdmins(), runAbandoned()]);
      setOrders(o.orders);
      setAdmins(a.admins);
      setAbandoned(ab.orders);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAssign(o: OrderRow, value: string) {
    const adminId = value === UNASSIGNED ? null : value;
    setPending(o.id);
    try {
      await runAssign({ data: { submissionId: o.id, adminId } });
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to assign");
    } finally {
      setPending(null);
    }
  }

  async function handleDownloadReport(o: OrderRow) {
    setRowBusy(o.id);
    try {
      const res = await runGetReportUrl({ data: { submissionId: o.id } });
      if (res.ok && res.url) window.open(res.url, "_blank", "noopener");
      else alert("Completion report not available");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed");
    } finally {
      setRowBusy(null);
    }
  }

  async function handleResendCompletion(o: OrderRow) {
    if (!confirm("Resend completion email to the founder?")) return;
    setRowBusy(o.id);
    try {
      const res = await runResendCompletion({ data: { submissionId: o.id } });
      if (res.emailed) {
        await refresh();
        alert("Completion email resent.");
      } else {
        alert(res.error ?? "Failed to send email");
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed");
    } finally {
      setRowBusy(null);
    }
  }

  function relativeTime(iso: string | null): string {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  }

  if (roleStatus !== "ok") return null;

  const overdueCount = orders.filter(isOverdue).length;

  const dateOf = (o: OrderRow): number => {
    const iso = o.paid_at ?? o.created_at;
    const t = iso ? new Date(iso).getTime() : NaN;
    return isNaN(t) ? 0 : t;
  };
  const deadlineOf = (o: OrderRow): number => {
    if (o.fulfillment_status === "completed") return Number.MAX_SAFE_INTEGER;
    const base = dateOf(o);
    return base === 0 ? Number.MAX_SAFE_INTEGER : base + 48 * 60 * 60 * 1000;
  };

  // Paid orders live in this table; pending and abandoned checkouts are the unpaid table below.
  const showPaidTable = payFilter === "all" || payFilter === "paid";
  const showAbandonedTable = payFilter === "all" || payFilter === "pending" || payFilter === "abandoned";

  const visible = orders
    .filter((o) => (overdueOnly ? isOverdue(o) : true))
    .filter((o) => (fulfilFilter === "all" ? true : o.fulfillment_status === fulfilFilter))
    .filter((o) => {
      if (assigneeFilter === "all") return true;
      if (assigneeFilter === UNASSIGNED) return !o.assigned_admin_id;
      return o.assigned_admin_id === assigneeFilter;
    })
    .slice()
    .sort((a, b) => {
      if (sortBy === "company") return a.company.localeCompare(b.company);
      if (sortBy === "deadline") return deadlineOf(a) - deadlineOf(b);
      if (sortBy === "oldest") return dateOf(a) - dateOf(b);
      return dateOf(b) - dateOf(a);
    });

  return (
    <>
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-script text-accent text-[22px] leading-none">fulfillment</p>
          <h1 className="font-display text-[28px] font-semibold mt-2">Orders</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAddUserOpen(true)}
            className="inline-flex items-center rounded-full border border-line px-4 py-1.5 text-[13px] hover:border-ink"
          >
            Add user
          </button>
          <button
            type="button"
            onClick={() => setAddTaskOpen(true)}
            className="inline-flex items-center rounded-full bg-accent text-accent-ink px-4 py-1.5 text-[13px] font-semibold hover:-translate-y-px transition"
          >
            Add task
          </button>
        </div>
      </div>

      <AddUserDialog open={addUserOpen} onOpenChange={setAddUserOpen} />
      <AddTaskDialog open={addTaskOpen} onOpenChange={setAddTaskOpen} onCreated={refresh} />


      {overdueCount > 0 ? (
        <div className="mb-4 rounded-2xl border border-red/40 bg-red/10 text-red px-4 py-3 text-[14px] flex items-center justify-between gap-3">
          <span>
            ⚠ {overdueCount} paid order{overdueCount === 1 ? "" : "s"} overdue - past the 48h SLA and not yet completed.
          </span>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={payFilter} onValueChange={(v) => setPayFilter(v as typeof payFilter)}>
          <SelectTrigger className="h-9 text-[13px] w-[170px]">
            <SelectValue placeholder="Payment status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payments</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="abandoned">Abandoned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fulfilFilter} onValueChange={(v) => setFulfilFilter(v as typeof fulfilFilter)}>
          <SelectTrigger className="h-9 text-[13px] w-[170px]">
            <SelectValue placeholder="Fulfillment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fulfillment</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="h-9 text-[13px] min-w-[190px]">
            <SelectValue placeholder="Assigned to" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {admins.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
          <SelectTrigger className="h-9 text-[13px] w-[180px]">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="deadline">Deadline soonest</SelectItem>
            <SelectItem value="company">Company A-Z</SelectItem>
          </SelectContent>
        </Select>
        <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer ml-1">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
            className="accent-red"
          />
          Overdue only
        </label>
      </div>

      {showPaidTable ? (
      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-surface-sunken text-ink-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Company</th>
              <th className="text-left px-4 py-3 font-medium">Founder</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Programs</th>
              <th className="text-left px-4 py-3 font-medium">Assignee</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-ink-tertiary">
                  Loading…
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-ink-tertiary">
                  {overdueOnly ? "No overdue orders." : "No paid orders yet."}
                </td>
              </tr>
            ) : (
              visible.map((o) => {
                const overdue = isOverdue(o);
                return (
                  <tr key={o.id} className="border-t border-line">
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{o.company}</span>
                        {overdue ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red/10 text-red border border-red/30">
                            OVERDUE
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-secondary">{o.founder_email}</td>
                    <td className="px-4 py-3 text-ink-secondary">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-4 py-3 text-ink-secondary">
                      {o.targets.total === 0 ? (
                        <span className="text-ink-tertiary">-</span>
                      ) : (
                        <span>
                          {o.targets.submitted}✓ / {o.targets.pending} pending / {o.targets.not_a_fit}✗
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={o.assigned_admin_id ?? UNASSIGNED}
                        onValueChange={(v) => handleAssign(o, v)}
                        disabled={pending === o.id}
                      >
                        <SelectTrigger className="h-8 text-[13px] min-w-[180px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                          {admins.map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={o.fulfillment_status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-2 justify-end flex-wrap">
                        {o.fulfillment_status === "completed" ? (
                          <>
                            {o.completion_report_pdf_path ? (
                              <button
                                type="button"
                                onClick={() => handleDownloadReport(o)}
                                disabled={rowBusy === o.id}
                                className="inline-flex items-center rounded-full bg-accent text-accent-ink px-3 py-1 text-[12px] font-semibold hover:-translate-y-px transition disabled:opacity-60"
                              >
                                Download report
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => handleResendCompletion(o)}
                              disabled={rowBusy === o.id}
                              className="inline-flex items-center rounded-full border border-line px-3 py-1 text-[12px] hover:border-ink disabled:opacity-60"
                              title={
                                o.completion_emailed_at
                                  ? `Last emailed: ${relativeTime(o.completion_emailed_at)}`
                                  : "Not yet emailed"
                              }
                            >
                              Resend email
                            </button>
                          </>
                        ) : null}
                        <Link
                          to="/tasks/$submissionId"
                          params={{ submissionId: o.id }}
                          className="text-[13px] font-medium text-accent-ink hover:underline"
                        >
                          Open →
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      ) : null}

      {showAbandonedTable ? (
      <>
      <div className="mt-10 mb-3 flex items-baseline gap-3 flex-wrap">
        <h2 className="font-display text-[20px] font-semibold">Abandoned checkouts (unpaid)</h2>
        <span className="text-[12px] text-ink-tertiary">
          Payment link created but never paid · not revenue, not fulfilled
        </span>
      </div>
      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-surface-sunken text-ink-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Company</th>
              <th className="text-left px-4 py-3 font-medium">Founder</th>
              <th className="text-left px-4 py-3 font-medium">Amount</th>
              <th className="text-left px-4 py-3 font-medium">Created</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-ink-tertiary">
                  Loading…
                </td>
              </tr>
            ) : abandoned.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-ink-tertiary">
                  No abandoned checkouts.
                </td>
              </tr>
            ) : (
              abandoned.map((a) => (
                <tr key={a.id} className="border-t border-line">
                  <td className="px-4 py-3 font-medium">{a.company}</td>
                  <td className="px-4 py-3 text-ink-secondary">{a.founder_email}</td>
                  <td className="px-4 py-3 text-ink-secondary tabular-nums">
                    {fmtAmount(a.amount_cents, a.currency)}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {a.created_at ? new Date(a.created_at).toLocaleString() : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface-sunken text-ink-tertiary border border-line">
                      Unpaid — abandoned
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </>
      ) : null}
    </>
  );
}

